import {
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";

import {
  type BrowseFilters,
  type BrowseSort,
  clientFacets,
  hasPriceBounds,
  type ServerFacet,
} from "@/lib/browse/browseFilter";

/**
 * How the public storefront and show table follow the server's price sort (TRO-2346, W6).
 *
 * TRO-2345 lets the server order and bound by the stored public price wherever
 * `stored_public_prices_enabled` is on, and says so as `server_price_sort` on every paged response.
 * Until the cutover, a new build can also reach Render (setting off) or an API from before TRO-2345,
 * which do not. Each hook follows what its last paged response said:
 *
 * - `server`: the price sort and bounds go to the server, and nothing walks for them.
 * - `client`: main's path. No price parameter is sent, and a price sort or bound walks the rest of
 *   the shop so the client can order and filter it. It is used where the server said
 *   `server_price_sort` false or absent, and after a page-1 refusal that is not `prices_hidden`.
 * - `hidden`: a shop or table that shows no prices (Q15). No price parameter is sent and nothing
 *   walks for price; the screen keeps its own price rules over the cards it loads.
 *
 * With no paged response yet (a deep link), the hook asks the server and falls back on page 1:
 * `fetchFirstPage` redoes the load as the answer requires, so a refused or unsorted page never
 * reaches the screen.
 */
export type PricePlan = "server" | "client" | "hidden";

/** What the last paged response said. `null` is "no paged response yet". */
export type ServerPriceKnowledge = {
  serverSorts: boolean | null;
  pricesHidden: boolean | null;
  /** The `server_facets` it listed (TRO-2347); empty from a server older than them. A load sends
   *  the game and set facets listed here when it starts, and walks for the rest. */
  serverFacets: readonly string[] | null;
};

/** The shopper's order and price bounds, plus the game and set facets set, which walk wherever the
 *  server does not list them in `server_facets` (TRO-2347). */
export type PriceIntent = {
  sort: BrowseSort;
  minCents: number | null;
  maxCents: number | null;
  facets: ServerFacet[];
};

/** The paged envelope fields this module reads. */
export type PricedPage = {
  next_cursor?: string | null;
  priced_as_of?: string | null;
  prices_changed_at?: string | null;
  server_price_sort?: boolean;
  server_facets?: string[];
  price_visibility?: string;
};

/** A loaded page and the plan it was requested under, which its cursor's next page reuses. */
export type PlannedPage<P> = { page: P; plan: PricePlan; };
export type PlannedCursor = { cursor: string; plan: PricePlan; };

export function priceIntent(sort: BrowseSort, filters: BrowseFilters): PriceIntent {
  return {
    sort,
    minCents: filters.minCents ?? null,
    maxCents: filters.maxCents ?? null,
    facets: clientFacets(filters),
  };
}

const priced = (intent: PriceIntent) => intent.sort.startsWith("price-") || hasPriceBounds(intent);

/** The plan a new load starts under. Only a server that said it sorts, or one not heard from
 *  yet, is sent price parameters. */
export function choosePlan(known: ServerPriceKnowledge, intent: PriceIntent): PricePlan {
  if (known.pricesHidden) return "hidden";
  if (known.serverSorts === false) return "client";
  return priced(intent) ? "server" : "client";
}

/** Whether a load walks the rest of the shop: for a game or set the server did not list in
 *  `served` (its `server_facets`), and for price only on main's path. */
export function walks(
  plan: PricePlan,
  intent: PriceIntent,
  served: readonly string[] = [],
): boolean {
  return intent.facets.some((facet) => !served.includes(facet))
    || (plan === "client" && priced(intent));
}

/** The `sort`, `price_min` and `price_max` parameters a plan sends. Name order is always the
 *  server's; bounds go as decimal amounts (C11). */
export function priceParams(plan: PricePlan, intent: PriceIntent): [string, string][] {
  const params: [string, string][] = [];
  if (intent.sort === "name") params.push(["sort", "name"]);
  if (plan !== "server") return params;
  if (intent.sort === "price-asc" || intent.sort === "price-desc") {
    params.push(["sort", intent.sort]);
  }
  if (intent.minCents != null) params.push(["price_min", (intent.minCents / 100).toFixed(2)]);
  if (intent.maxCents != null) params.push(["price_max", (intent.maxCents / 100).toFixed(2)]);
  return params;
}

/** A cursor page the server refused with 422 (A6): the hook restarts the list from the top. */
export class CursorRefusedError extends Error {
  constructor() {
    super("The server refused this page's cursor");
    this.name = "CursorRefusedError";
  }
}

async function errorCode(response: Response): Promise<string | null> {
  try {
    const body: unknown = await response.json();
    return typeof body === "object" && body !== null && "code" in body
        && typeof body.code === "string"
      ? body.code
      : null;
  } catch {
    return null;
  }
}

function learn(known: ServerPriceKnowledge, page: PricedPage) {
  known.serverSorts = page.server_price_sort === true;
  known.serverFacets = page.server_facets ?? [];
  known.pricesHidden = page.price_visibility === undefined
    ? null
    : page.price_visibility === "none";
}

/**
 * Loads page 1 under the plan `known` allows, applying the page-1 rules to a request that carried
 * price parameters:
 *
 * - a 200 that says `server_price_sort: true` is used;
 * - a 200 without it (Render, or an API from before TRO-2345, which ignores `price_min`) is
 *   dropped, and page 1 is loaded again as main loads it;
 * - a 422 `prices_hidden` reloads page 1 once without price parameters, and does not walk;
 * - any other 422 (`price_sort_off`, or FastAPI's validation 422 from an API without the price
 *   sorts) marks the server as not sorting and loads page 1 as main does.
 *
 * `request` sends page 1 under a plan; `parse` validates a 200 and `fail` throws the hook's own
 * error for any other status. No error is shown for a fallback.
 */
export async function fetchFirstPage<P extends PricedPage>(
  known: ServerPriceKnowledge,
  intent: PriceIntent,
  request: (plan: PricePlan) => Promise<Response>,
  parse: (response: Response) => Promise<P>,
  fail: (response: Response) => never,
): Promise<PlannedPage<P>> {
  const load = async (plan: PricePlan): Promise<PlannedPage<P>> => {
    const response = await request(plan);
    if (!response.ok) fail(response);
    const page = await parse(response);
    learn(known, page);
    return { page, plan };
  };
  const plan = choosePlan(known, intent);
  if (plan !== "server" || !priced(intent)) return load(plan);

  const response = await request("server");
  if (response.ok) {
    const page = await parse(response);
    learn(known, page);
    if (page.server_price_sort === true) return { page, plan };
    return load("client");
  }
  if (response.status !== 422) fail(response);
  if (await errorCode(response) === "prices_hidden") {
    known.pricesHidden = true;
    return load("hidden");
  }
  known.serverSorts = false;
  return load("client");
}

/** Loads a later page under its page 1's plan. A 422 is the A6 restart, not an error. */
export async function fetchCursorPage<P extends PricedPage>(
  known: ServerPriceKnowledge,
  plan: PricePlan,
  request: () => Promise<Response>,
  parse: (response: Response) => Promise<P>,
  fail: (response: Response) => never,
): Promise<PlannedPage<P>> {
  const response = await request();
  if (response.status === 422) throw new CursorRefusedError();
  if (!response.ok) fail(response);
  const page = await parse(response);
  learn(known, page);
  return { page, plan };
}

export const nextPlannedCursor = <P extends PricedPage>(
  last: PlannedPage<P>,
): PlannedCursor | undefined =>
  last.page.next_cursor ? { cursor: last.page.next_cursor, plan: last.plan } : undefined;

/** Whether a later page reports a newer `prices_changed_at` than page 1 did. A null on either
 *  side raises nothing, so a shop that shows no prices never does. */
export function pricesChangedSince(pages: readonly PricedPage[]): boolean {
  const first = pages[0]?.prices_changed_at;
  if (!first) return false;
  const since = Date.parse(first);
  return pages.slice(1).some((page) =>
    !!page.prices_changed_at && Date.parse(page.prices_changed_at) > since
  );
}

type PlannedQuery<P> = UseInfiniteQueryResult<InfiniteData<PlannedPage<P>>>;

/** What one hook instance has learned. Per instance, so it outlives a change of sort or filter
 *  (W6: the last paged response); `queryFn` reads and updates it. */
export function useServerPriceKnowledge(): ServerPriceKnowledge {
  return useRef<ServerPriceKnowledge>({ serverSorts: null, pricesHidden: null, serverFacets: null })
    .current;
}

/**
 * The price bounds as the query key carries them. While the server may take the bounds, they are
 * part of the request, so a change refetches. Once it is known not to (it does not sort, or the
 * shop shows no prices), the key keeps the bounds it had then: editing a bound re-filters the
 * loaded cards with no request, as main did, and learning it mid-load does not fetch page 1 twice.
 */
export function usePriceBoundsKey(known: ServerPriceKnowledge, intent: PriceIntent): string {
  const keyed = useRef<string | null>(null);
  const serverIgnoresBounds = known.serverSorts === false || known.pricesHidden === true;
  if (keyed.current === null || !serverIgnoresBounds) {
    keyed.current = JSON.stringify([intent.minCents, intent.maxCents]);
  }
  return keyed.current;
}

/**
 * The parts of following the server's price sort that live beside the hook's `useInfiniteQuery`:
 * the plan the loaded pages answer, the A6 restart, and the freshness cues.
 */
export function useServerPriceSort<P extends PricedPage>(
  known: ServerPriceKnowledge,
  queryKey: QueryKey,
  query: PlannedQuery<P>,
  intent: PriceIntent,
  served: readonly string[] = [],
) {
  const queryClient = useQueryClient();
  const pages = query.data?.pages;
  // The placeholder pages answer the previous query, so until this one lands, plan from what is
  // known now.
  const settled = !query.isPlaceholderData && !!pages?.length;
  const plan = settled ? pages[0].plan : choosePlan(known, intent);

  // A6: a 422 on a cursor page drops the loaded pages and reloads page 1, once per load. A second
  // refusal in the same load is left as the page error; page 1's own refusals take the page-1 rules.
  const keyString = JSON.stringify(queryKey);
  const restartedFor = useRef<string | null>(null);
  const { isFetchNextPageError, error } = query;
  useEffect(() => {
    if (!isFetchNextPageError || !(error instanceof CursorRefusedError)) return;
    if (restartedFor.current === keyString) return;
    restartedFor.current = keyString;
    void queryClient.resetQueries({ queryKey, exact: true });
  }, [isFetchNextPageError, error, keyString, queryClient, queryKey]);

  const { refetch } = query;
  // The pill's tap (Q12): back to page 1 alone, refetched with no cursor.
  const refreshPrices = useCallback(() => {
    restartedFor.current = null;
    queryClient.setQueryData<InfiniteData<PlannedPage<P>>>(
      queryKey,
      (data) => data && { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) },
    );
    void refetch();
  }, [queryClient, queryKey, refetch]);

  return {
    plan,
    walking: walks(plan, intent, served),
    pricedAsOf: settled ? pages[0].page.priced_as_of ?? null : null,
    pricesUpdated: settled && pricesChangedSince(pages.map((planned) => planned.page)),
    refreshPrices,
  };
}
